import dynamic from 'next/dynamic';
import { NextSeo } from 'next-seo';
import { Fragment, useState, useRef } from 'react';
import { useLocationState } from '../../context/location';
import { submitClientRequest } from '../../helpers/clientRequest';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import Intro from '../../components/Intro';
import Splitter from '../../components/Splitter';
import ServicesList from '../../components/services/ServicesList';
import styles from '../../styles/modules/services/Services.module.scss';
import FormAgreement from '../../components/FormAgreement';

const Modal = dynamic(() => import('../../components/Modal'));

const ServicesPage = () => {
  const locationState = useLocationState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();

  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        clientComment,
        pathName: '/services',
        whatComponent: 'Services',
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) setIsSubmited(true);
      });
    }
  };

  return (
    <Fragment>
      <NextSeo
        title="Услуги агентства недвижимости «ЛЕНДЛОРД»"
        description="Услуги агентства недвижимости «ЛЕНДЛОРД» в Ростовской области: юридические услуги, оформление документов."
        openGraph={{
          url: 'https://landlord.ru',
          type: 'website',
          title: 'Услуги агентства недвижимости «ЛЕНДЛОРД»',
          description:
            'Услуги агентства недвижимости «ЛЕНДЛОРД» в Ростовской области: юридические услуги, оформление документов.',
          images: [
            {
              url: '/images/og-preview.png'
            }
          ],
          site_name: 'LANDLORD'
        }}
      />
      <Header />
      <main>
        <Intro
          title="Все операции с недвижимостью"
          description="Команда профессионалов, готовых освободить ваше время и взять на себя заботы по оформлению недвижимости"
          image="/images/stock-7.webp"
          button="Получить консультацию"
          phone="+7 800 222 81 08"
          phoneURI="tel:+78632218484"
          onClick={() => setIsModalVisible(true)}
        />
        <Modal
          title="Получить консультацию"
          button={!isSubmited && 'Отправить'}
          action={handleFormSubmit}
          visibility={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          buttonDisabled={!agreeWithPolicy}
        >
          {isSubmited ? (
            <Fragment>
              <div style={{ marginBottom: 18 }}>
                Спасибо за вашу заявку. Мы скоро с Вами свяжемся!
              </div>
              <img
                src="/images/thumbUp.png"
                alt=""
                style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
              />
            </Fragment>
          ) : (
            <Fragment>
              <div style={{ marginBottom: 18 }}>
                Оставьте свои контактные данные и мы с Вами свяжемся в самые кратчайшие сроки,
                поможем решить все имеющиеся у Вас вопросы!
              </div>
              <Input
                title="Имя"
                inputs={[
                  {
                    placeholder: 'Василий Петрович',
                    value: clientName,
                    type: 'text'
                  }
                ]}
                isWrappedStyle
                ref={clientNameId}
                onChange={(v) => setClientName(v[0])}
              />
              <Input
                title="Телефон"
                inputs={[
                  {
                    placeholder: locationState.phone,
                    value: clientPhone,
                    mask: 'phone',
                    type: 'tel'
                  }
                ]}
                isWrappedStyle
                ref={clientPhoneId}
                onChange={(v) => setClientPhone(v[0])}
              />
              <Textarea
                title="Коммментарий"
                placeholder="Например, «Мне в наследство перешла квартира в центре города, как юридически правильно ее переоформлять?»"
                autoHeight
                isWrappedStyle
                onChange={(v) => setClientComment(v)}
              />
              <FormAgreement onChange={setAgreeWithPolicy} />
            </Fragment>
          )}
        </Modal>
        <section className="block">
          <div className="fixed">
            <ServicesList />
          </div>
        </section>
        <section className="block">
          <div className="fixed">
            <p>
              С полным списком услуг и их стоимостью можно ознакомиться по{' '}
              <a href={'/downloads/suo.pdf?' + Date.now()} target="_blank">
                прямой ссылке
              </a>
            </p>
            <p className={styles.tip}>
              ¹ Цена указана без учета стоимости необходимых документов, оплаты государственных
              пошлин.
            </p>
            <p className={styles.tip}>
              ² Данный перечень не является исчерпывающим. По согласованию сторон, в соответствии с
              компетенцией сотрудников агентства, оказываются и иные виды услуг. При этом цены на
              такие услуги согласовываются сторонами дополнительно.
            </p>
            <p className={styles.tip}>
              ³ Оказание услуг осуществляется на основании заключенных договоров и выдачи
              нотариальной доверенности клиентом на сотрудника Агентства недвижимости. Агентство
              оставляет за собой право предоставлять клиентам скидки.
            </p>
          </div>
        </section>
        <Splitter
          title="Требуется помощь?"
          subtitle="Укажите свой номер телефона и мы обязательно Вам поможем в кратчайшие сроки"
        />
      </main>
      <Footer seo="Консультационные и юридические услуги в Ростове-на-Дону, Азове, Новочеркасске и Батайске" />
    </Fragment>
  );
};

export default ServicesPage;
